import React, { useState } from 'react';
import { api } from './api/api';

// ── Stat row component ─────────────────────────────────────────
const StatRow = ({ label, value }) => (
    <tr>
        <td style={{ color: 'var(--c-text-muted)', fontWeight: 500, width: '45%' }}>{label}</td>
        <td style={{ fontWeight: 600 }}>{value ?? '—'}</td>
    </tr>
);

// ── Driver stats table ─────────────────────────────────────────
const DriverTable = ({ data }) => {
    if (!data) return null;
    return (
        <div className="fb-table-wrap" style={{ marginTop: 16 }}>
            <table className="fb-table fb-table-sm">
                <tbody>
                    <StatRow label="ID"               value={data.id} />
                    <StatRow label="Name"             value={data.name} />
                    <StatRow label="Nationality"      value={data.nat} />
                    <StatRow label="Overall"          value={data.overall} />
                    <StatRow label="Concentration"    value={data.concentration} />
                    <StatRow label="Talent"           value={data.talent} />
                    <StatRow label="Experience"       value={data.experience} />
                    <StatRow label="Aggressiveness"   value={data.aggressiveness} />
                    <StatRow label="Technical Insight" value={data.ti} />
                    <StatRow label="Stamina"          value={data.stamina} />
                    <StatRow label="Charisma"         value={data.charisma} />
                    <StatRow label="Motivation"       value={data.motivation} />
                    <StatRow label="Reputation"       value={data.reputation} />
                    <StatRow label="Age"              value={data.age} />
                    <StatRow label="Weight"           value={data.weight ? `${data.weight} kg` : null} />
                    <StatRow label="Ret"              value={data.ret} />
                    <StatRow label="Salary"           value={data.salary} />
                    <StatRow label="Fee"              value={data.fee} />
                    <StatRow label="Favourite"        value={data.fav} />
                    <StatRow label="Offers"           value={data.offers} />
                    <StatRow label="Noted"            value={data.noted} />
                </tbody>
            </table>
        </div>
    );
};

// ── TD stats table ─────────────────────────────────────────────
const TdTable = ({ data }) => {
    if (!data) return null;
    return (
        <div className="fb-table-wrap" style={{ marginTop: 16 }}>
            <table className="fb-table fb-table-sm">
                <tbody>
                    <StatRow label="ID"               value={data.id} />
                    <StatRow label="Name"             value={data.name} />
                    <StatRow label="Nationality"      value={data.nat} />
                    <StatRow label="Overall"          value={data.overall} />
                    <StatRow label="Leadership"       value={data.leadership} />
                    <StatRow label="Mechanics"        value={data.mechanics} />
                    <StatRow label="Electronics"      value={data.electronics} />
                    <StatRow label="Aerodynamics"     value={data.aerodynamics} />
                    <StatRow label="Experience"       value={data.experience} />
                    <StatRow label="Pit Coordination" value={data.pitcoordination} />
                    <StatRow label="Motivation"       value={data.motivation} />
                    <StatRow label="Age"              value={data.age} />
                    <StatRow label="Ret"              value={data.ret} />
                    <StatRow label="Salary"           value={data.salary} />
                    <StatRow label="Fee"              value={data.fee} />
                    <StatRow label="Offers"           value={data.offers} />
                    <StatRow label="Noted"            value={data.noted} />
                </tbody>
            </table>
        </div>
    );
};

// ── Search panel ───────────────────────────────────────────────
const SearchPanel = ({ title, idValue, onIdChange, onSearch, loading, error, children }) => (
    <div className="fb-card" style={{ flex: 1, minWidth: 0 }}>
        <div className="fb-card-header">
            <span className="fb-card-title">{title}</span>
        </div>
        <div className="fb-card-body">
            <div className="fb-row" style={{ marginBottom: 0 }}>
                <input
                    type="number"
                    className="fb-input"
                    placeholder="Enter ID…"
                    value={idValue}
                    onChange={(e) => onIdChange(e.target.value)}
                    style={{ flex: 1 }}
                    onKeyDown={(e) => e.key === 'Enter' && onSearch()}
                />
                <button
                    className="fb-btn fb-btn-primary"
                    onClick={onSearch}
                    disabled={loading}
                >
                    {loading ? 'Loading…' : 'Search'}
                </button>
            </div>
            {error   && <div className="fb-alert fb-alert-error" style={{ marginTop: 12 }}>{error}</div>}
            {children}
        </div>
    </div>
);

// ── Main component ─────────────────────────────────────────────
const DriverAndTDHistory = () => {
    const [driverId,      setDriverId]      = useState('');
    const [tdId,          setTdId]          = useState('');
    const [driverHistory, setDriverHistory] = useState(null);
    const [tdHistory,     setTdHistory]     = useState(null);
    const [driverError,   setDriverError]   = useState(null);
    const [tdError,       setTdError]       = useState(null);
    const [driverLoading, setDriverLoading] = useState(false);
    const [tdLoading,     setTdLoading]     = useState(false);

    const fetchDriverHistory = async () => {
        if (!driverId.trim()) return;
        setDriverLoading(true);
        setDriverError(null);
        try {
            const res = await api.get(`/drivers/${driverId}/history`);
            setDriverHistory(res.data);
        } catch (err) {
            console.error('Failed to fetch driver history:', err);
            setDriverError('Driver not found or error loading data.');
            setDriverHistory(null);
        } finally {
            setDriverLoading(false);
        }
    };

    const fetchTdHistory = async () => {
        if (!tdId.trim()) return;
        setTdLoading(true);
        setTdError(null);
        try {
            const res = await api.get(`/tds/${tdId}/history`);
            setTdHistory(res.data);
        } catch (err) {
            console.error('Failed to fetch TD history:', err);
            setTdError('TD not found or error loading data.');
            setTdHistory(null);
        } finally {
            setTdLoading(false);
        }
    };

    return (
        <>
            <div className="fb-page-header">
                <div className="fb-page-title">Driver &amp; TD History</div>
                <div className="fb-page-sub">Look up historical stats for drivers and technical directors</div>
            </div>

            <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <SearchPanel
                    title="Driver History"
                    idValue={driverId}
                    onIdChange={setDriverId}
                    onSearch={fetchDriverHistory}
                    loading={driverLoading}
                    error={driverError}
                >
                    <DriverTable data={driverHistory} />
                </SearchPanel>

                <SearchPanel
                    title="TD History"
                    idValue={tdId}
                    onIdChange={setTdId}
                    onSearch={fetchTdHistory}
                    loading={tdLoading}
                    error={tdError}
                >
                    <TdTable data={tdHistory} />
                </SearchPanel>
            </div>
        </>
    );
};

export default DriverAndTDHistory;
